import { add, format, parseISO } from "date-fns";
import { rawIntervalToSeconds } from "../../utils/interval";

import type { Database } from "../../__definitions__/database-definitions";
import type { IntervalJson } from "../../utils/interval";

/** A Communication of a program, with its timing, from the moment_timeline_with_archived view. */
export type TimelineRow = Database["public"]["Views"]["moment_timeline_with_archived"]["Row"];

/** One timing on a group's rail, such as "2 weeks before", and the Communications at it. */
interface TimelineStop {
  key: string;
  title: string;
  /** The timing in a few characters, such as "−14d", for the list. */
  short: string;
  detail: string;
  /** The day of a person's date, or today on the specific dates, which the rail is drawn from. */
  anchor: boolean;
  rows: TimelineRow[];
}

/** The Communications that send from one event, such as a person's start date. */
export interface TimelineGroup {
  key: string;
  name: string;
  /** The date's key, for a person's date. */
  code: string | null;
  /** The colour of the group's dot, rail and chip. */
  color: string;
  stops: TimelineStop[];
  count: number;
}

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

export const communicationsText = (n: number): string => plural(n, "communication");

interface Offset {
  years: number;
  months: number;
  weeks: number;
  days: number;
}

const offsetOf = (json: IntervalJson | null): Offset => ({
  years: json?.years ?? 0,
  months: json?.months ?? 0,
  weeks: json?.weeks ?? 0,
  days: json?.days ?? 0,
});

/**
 * "2 weeks before", "1 month, 3 days after", or "" with no offset. Days that make whole weeks are
 * weeks.
 */
export const offsetText = (json: IntervalJson | null): string => {
  const offset = offsetOf(json);
  if (offset.days && !offset.weeks && offset.days % 7 === 0) {
    offset.weeks = offset.days / 7;
    offset.days = 0;
  }
  const parts = (["years", "months", "weeks", "days"] as const)
    .filter((unit) => offset[unit] !== 0)
    .map((unit) => plural(Math.abs(offset[unit]), unit.slice(0, -1)));
  if (!parts.length) return "";
  return `${parts.join(", ")} ${rawIntervalToSeconds(json) < 0 ? "before" : "after"}`;
};

/** "−14d", "+1mo 3d", or "Day 0" with no offset. */
export const shortOffsetText = (json: IntervalJson | null): string => {
  const offset = offsetOf(json);
  const parts = (
    [
      ["years", "y"],
      ["months", "mo"],
      ["weeks", "w"],
      ["days", "d"],
    ] as const
  )
    .filter(([unit]) => offset[unit] !== 0)
    .map(([unit, letter]) => `${Math.abs(offset[unit])}${letter}`);
  if (!parts.length) return "Day 0";
  return `${rawIntervalToSeconds(json) < 0 ? "−" : "+"}${parts.join(" ")}`;
};

/** "9:00am", from a time such as "09:00:00". */
export const sendTimeText = (time: string | null): string =>
  time ? format(parseISO(`2000-01-01T${time}`), "h:mmaaa") : "";

const byTime = (a: TimelineRow, b: TimelineRow): number =>
  (a.send_at ?? "").localeCompare(b.send_at ?? "") || a.title.localeCompare(b.title);

/** Puts each row in the stop of its key, and sorts the stops and the rows in each. */
const toStops = (
  rows: TimelineRow[],
  stopOf: (row: TimelineRow) => {
    key: string;
    title: string;
    short: string;
    order: number | string;
  },
): TimelineStop[] => {
  const stops = new Map<
    string,
    { title: string; short: string; order: number | string; rows: TimelineRow[] }
  >();
  for (const row of rows) {
    const { key, title, short, order } = stopOf(row);
    const stop = stops.get(key) ?? { title, short, order, rows: [] };
    stop.rows.push(row);
    stops.set(key, stop);
  }
  return [...stops.entries()]
    .toSorted(([, a], [, b]) =>
      typeof a.order === "number" && typeof b.order === "number"
        ? a.order - b.order
        : String(a.order).localeCompare(String(b.order)),
    )
    .map(([key, stop]) => ({
      key,
      title: stop.title,
      short: stop.short,
      detail: communicationsText(stop.rows.length),
      anchor: false,
      rows: stop.rows.toSorted(byTime),
    }));
};

/** The stops, with the stop before the first one that comes after it. */
const insertStop = (
  stops: TimelineStop[],
  stop: TimelineStop,
  after: (other: TimelineStop) => boolean,
): TimelineStop[] => {
  const i = stops.findIndex(after);
  return i === -1 ? [...stops, stop] : [...stops.slice(0, i), stop, ...stops.slice(i)];
};

/**
 * The stops of a person's date, by offset. The day of the date always has a stop, as the rail's
 * anchor, also with no Communication on it.
 */
const subjectStops = (rows: TimelineRow[], name: string): TimelineStop[] => {
  const on = `On the ${name.toLowerCase()}`;
  const stops = toStops(rows, (row) => {
    const seconds = rawIntervalToSeconds(row.offset_json);
    return {
      key: String(seconds),
      title: seconds ? offsetText(row.offset_json) : on,
      short: shortOffsetText(row.offset_json),
      order: seconds,
    };
  });
  const own = stops.find(({ key }) => key === "0");
  if (own) {
    own.anchor = true;
    return stops;
  }
  const day: TimelineStop = {
    key: "0",
    title: on,
    short: "Day 0",
    detail: "Day 0",
    anchor: true,
    rows: [],
  };
  return insertStop(stops, day, ({ key }) => Number(key) > 0);
};

/** The date that a fixed Communication sends on: the account date it sends from, moved by the offset. */
const fixedDate = (row: TimelineRow): Date | null =>
  row.first_date ? add(parseISO(row.first_date), offsetOf(row.offset_json)) : null;

/**
 * The specific dates' stops, with today's as the anchor: the stop of today's date, or a stop of its
 * own in date order when nothing sends today. The stop with no date stays last.
 */
const withToday = (stops: TimelineStop[], today: Date): TimelineStop[] => {
  const key = format(today, "yyyy-MM-dd");
  const own = stops.find((stop) => stop.key === key);
  if (own) {
    own.title = "Today";
    own.detail = `${format(today, "EEE d MMM yyyy")} · ${own.detail}`;
    own.anchor = true;
    return stops;
  }
  const day: TimelineStop = {
    key,
    title: "Today",
    short: format(today, "d MMM yyyy"),
    detail: `${format(today, "EEEE d MMMM yyyy")} · nothing sends today`,
    anchor: true,
    rows: [],
  };
  return insertStop(stops, day, (stop) => stop.key === "none" || stop.key > key);
};

const repeatText = (repeat: string | null): string => {
  if (!repeat) return "Once";
  if (/^1 years?$/.test(repeat)) return "Every year";
  if (/^1 mons?$/.test(repeat)) return "Every month";
  if (repeat === "7 days") return "Every week";
  if (repeat === "1 day") return "Every day";
  return `Repeats every ${repeat.replace(/mons?/, "months")}`;
};

/** The colours of the groups of people's dates, in turn. */
const SUBJECT_COLORS = ["#5048E5", "#C208B1", "#2E7D5B", "#BE123C", "#9333EA"];

const OTHER_GROUPS = [
  {
    kind: "fixed",
    name: "Specific date",
    color: "#64748B",
    stop: (row: TimelineRow) => {
      const date = fixedDate(row);
      return date
        ? {
            key: format(date, "yyyy-MM-dd"),
            title: format(date, "EEE d MMM yyyy"),
            short: format(date, "d MMM yyyy"),
            order: format(date, "yyyy-MM-dd"),
          }
        : { key: "none", title: "No date yet", short: "No date", order: "9999-12-31" };
    },
  },
  {
    kind: "repeating",
    name: "Recurring",
    color: "#0E7490",
    stop: (row: TimelineRow) => {
      const title = [row.schedule_event_title ?? "Repeating", offsetText(row.offset_json)]
        .filter(Boolean)
        .join(", ");
      return {
        key: `${title} ${row.repeats_every ?? ""}`,
        title,
        short: [
          repeatText(row.repeats_every),
          rawIntervalToSeconds(row.offset_json) ? shortOffsetText(row.offset_json) : "",
        ]
          .filter(Boolean)
          .join(" "),
        order: title,
      };
    },
  },
  {
    kind: "publish",
    name: "When published",
    color: "#B45309",
    stop: () => ({ key: "publish", title: "When published", short: "On publish", order: 0 }),
  },
  {
    kind: "other",
    name: "Other timing",
    color: "#6B7280",
    stop: (row: TimelineRow) => {
      const title = offsetText(row.offset_json) || "On the date";
      return {
        key: title,
        title,
        short: rawIntervalToSeconds(row.offset_json) ? shortOffsetText(row.offset_json) : title,
        order: rawIntervalToSeconds(row.offset_json),
      };
    },
  },
  {
    kind: "none",
    name: "No timing yet",
    color: "#9CA3AF",
    stop: () => ({ key: "none", title: "No timing yet", short: "—", order: 0 }),
  },
] as const;

/**
 * The Communications in groups by the event they send from: each person's date by its name, then
 * specific dates, recurring ones, ones that send when published, others, and ones with no timing.
 * In each group, a stop for each timing, in order. A repeating stop says how often it repeats, and
 * the specific dates have a stop for today.
 */
export const buildTimeline = (rows: TimelineRow[], today: Date): TimelineGroup[] => {
  const subjects = new Map<string, TimelineRow[]>();
  for (const row of rows) {
    if (row.kind !== "subject" || !row.subject_date_key) continue;
    subjects.set(row.subject_date_key, [...(subjects.get(row.subject_date_key) ?? []), row]);
  }

  const sorted = [...subjects.entries()]
    .map(([key, own]) => ({ key, own, name: own[0]?.subject_date_name ?? key }))
    .toSorted((a, b) => a.name.localeCompare(b.name));
  // An event with only archived Communications takes its colour after the others, so that the
  // archived ones do not change the others' colours when they load.
  const live = ({ own }: (typeof sorted)[number]): boolean =>
    own.some(({ status }) => status !== "archived");
  const colourOrder = [...sorted.filter(live), ...sorted.filter((group) => !live(group))];
  const subjectGroups = sorted.map((group): TimelineGroup => ({
    key: `subject:${group.key}`,
    name: group.name,
    code: group.key,
    color: SUBJECT_COLORS[colourOrder.indexOf(group) % SUBJECT_COLORS.length] ?? "",
    stops: subjectStops(group.own, group.name),
    count: group.own.length,
  }));

  const otherGroups = OTHER_GROUPS.flatMap(({ kind, name, color, stop }): TimelineGroup[] => {
    const own = rows.filter((row) =>
      kind === "other"
        ? row.kind === "other" || (row.kind === "subject" && !row.subject_date_key)
        : row.kind === kind,
    );
    if (!own.length) return [];
    const stops = kind === "fixed" ? withToday(toStops(own, stop), today) : toStops(own, stop);
    if (kind === "repeating") {
      for (const item of stops)
        item.detail = `${repeatText(item.rows[0]?.repeats_every ?? null)} · ${item.detail}`;
    }
    return [{ key: kind, name, code: null, color, stops, count: own.length }];
  });

  return [...subjectGroups, ...otherGroups];
};
